import { Component, ElementRef, inject, type OnDestroy, type OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { AppService, VersionService } from '@core';
import { Store } from '@ngrx/store';
import { AuthActions } from '@ngrx-store';
import { CopyrightFooterComponent } from '@ui';
import { StartSettingsClass } from '@users';

import { MainMenuComponent } from './components/main-menu/main-menu.component';
import { openUserActivityDialog, UserActivityModalComponent } from './components/user-activity-modal/user-activity-modal.component';
import { IdleUserService } from './services';

@Component({
  imports: [CopyrightFooterComponent, RouterModule, MainMenuComponent],
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
})
export class AppComponent extends StartSettingsClass implements OnInit, OnDestroy {
  override store: Store;
  override router: Router;
  override appService: AppService;

  @ViewChild('main') main: ElementRef | undefined;
  showActivityModal = false;
  dialogRef: MatDialogRef<UserActivityModalComponent> | null = null;
  userSessionTimeoutMinutes = 0;

  private dialog = inject(MatDialog);
  private idleUserService = inject(IdleUserService);
  private versionService = inject(VersionService);

  constructor() {
    const store = inject(Store);
    const router = inject(Router);
    const appService = inject(AppService);

    super(store, router, appService);
    this.store = store;
    this.router = router;
    this.appService = appService;

    this.onUserSelected = (isLoggedIn, user) => {
      if (isLoggedIn && !this.appService.isInitailizedAppSubscription) {
        this.appService.isInitailizedAppSubscription = true;
        this.userSessionTimeoutMinutes = user.homeClientUserSettings.userSessionTimeoutMinutes;
        this.initIdleWatching(this.userSessionTimeoutMinutes * 60);
      }
    };
    this.onNavigationEnd = () => {
      if (this.main) {
        this.main.nativeElement.scrollTop = 0;
      }
    };
  }

  scrollListener() {
    const autocompleteInput = document.querySelector('.autocomplete-input');

    if (autocompleteInput instanceof HTMLElement) {
      autocompleteInput.blur();
    }
  };

  async ngOnInit() {
    this.init();

    this.idleUserService.idle.onIdleStart.subscribe(() => {
      if (!this.showActivityModal) {
        this.showActivityModal = true;
        this.dialogRef = openUserActivityDialog(this.dialog, 31, this.userSessionTimeoutMinutes);
      }
    });

    this.idleUserService.idle.onIdleEnd.subscribe(() => {
      this.dialogRef?.close();
      this.dialogRef = null;
      this.showActivityModal = false;
    });

    this.idleUserService.idle.onTimeout.subscribe(() => {
      this.showActivityModal = false;
      this.idleUserService.stopWatching();
      this.dialog.closeAll();
      this.store.dispatch(AuthActions.logout());
    });

    await this.versionService.apply();
  }

  ngOnDestroy() {
    this.idleUserService.stopWatching();
  }

  initIdleWatching(idleTimeSeconds: number) {
    this.idleUserService.setIdleTime(idleTimeSeconds);
    this.idleUserService.startWatching();
  }
}
